import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import LoadingOverlay from "../LoadingOverlay";
import EmailTagInput from '../Mail/EmailTagInput';

const MailParams = ({
    sender,
    password,
    to,
    cc,
    cci,
    subject,
    text,
    files,
    selectedDossier,
    disableButton,
    handleChange,
    handleFileChange,
    handleRemoveFile,
    handleOpenDialog,
    handleAutoSelect,
    handleResetForm,
    showPDF,
    showImage,
    getFileType,
    fileMetadata
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [buttonStates, setButtonStates] = useState({
        devis: { hasFiles: false },
        contrat: { hasFiles: false },
        pgc: { hasFiles: false },
        prediuo: { hasFiles: false },
        diuo: { hasFiles: false },
        vic: { hasFiles: false },
        rj: { hasFiles: false }
    });

    const useDossier = () => useSelector((state) => state.dossier.items, shallowEqual);
    const dossier = useDossier();

    // Couleurs pour chaque type de document
    const documentColors = {
        devis: '#3b82f6',
        contrat: '#10b981',
        pgc: '#8b5cf6',
        prediuo: '#f59e0b',
        diuo: '#ef4444',
        vic: '#06b6d4',
        rj: '#84cc16',
        manual: '#6b7280'
    };

    // Fonction locale pour déterminer le type de fichier si getFileType n'est pas fourni
    const getFileTypeLocal = (fileName) => {
        const lowerName = fileName.toLowerCase();
        if (lowerName.includes('devis')) return 'devis';
        if (lowerName.includes('contrat')) return 'contrat';
        if (lowerName.includes('pgc')) return 'pgc';
        if (lowerName.includes('prediuo') || lowerName.includes('pre-diuo')) return 'prediuo';
        if (lowerName.includes('diuo')) return 'diuo';
        if (lowerName.includes('vic')) return 'vic';
        if (lowerName.includes('rj')) return 'rj';
        return 'manual';
    };

    // Effet pour mettre à jour l'état des boutons en fonction des fichiers présents
    useEffect(() => {
        const newButtonStates = { ...buttonStates };

        Object.keys(newButtonStates).forEach(type => {
            newButtonStates[type] = {
                hasFiles: false
            };
        });

        files.forEach(file => {
            const fileType = getFileType ? getFileType(file.name) : getFileTypeLocal(file.name);
            if (fileType !== 'manual' && newButtonStates[fileType] !== undefined) {
                newButtonStates[fileType] = {
                    hasFiles: true
                };
            }
        });

        setButtonStates(newButtonStates);
    }, [files, getFileType]);

    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const handleReset = () => {
        if (handleResetForm) {
            handleResetForm();
        }
    };

    // Wrapper pour handleAutoSelect avec gestion des états
    const handleAutoSelectWithState = async (documentType) => {
        try {
            await handleAutoSelect(documentType);
        } catch (error) {
            console.log(`Erreur lors de la sélection ${documentType}:`, error.message);
        }
    };

    // Style de bouton avec couleur personnalisée
    const getButtonStyle = (documentType) => {
        const color = documentColors[documentType];
        const state = buttonStates[documentType];
        const isDisabled = disableButton || selectedDossier === '';

        return {
            margin: '0.75rem 0.25rem 0.5rem 0',
            padding: '0.75rem 1rem',
            border: `2px solid ${state.hasFiles ? color : 'transparent'}`,
            borderRadius: '8px',
            background: isDisabled ? '#f5f5f5' :
                state.hasFiles ? `${color}15` :
                    'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            color: isDisabled ? '#999' :
                state.hasFiles ? color : '#1f2937',
            fontSize: '0.8125rem',
            fontWeight: '600',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            boxShadow: isDisabled || state.hasFiles ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.05)',
            opacity: isDisabled ? 0.5 : 1,
            transform: 'translateY(0)',
            position: 'relative'
        };
    };

    const getButtonHoverHandlers = (documentType) => {
        const color = documentColors[documentType];
        const state = buttonStates[documentType];
        const isDisabled = disableButton || selectedDossier === '';
        const hasFiles = state.hasFiles;

        if (isDisabled || hasFiles) return {};

        return {
            onMouseEnter: (e) => {
                e.target.style.borderColor = color;
                e.target.style.color = color;
                e.target.style.background = `${color}10`;
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${color}25`;
            },
            onMouseLeave: (e) => {
                e.target.style.borderColor = 'transparent';
                e.target.style.color = '#1f2937';
                e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            }
        };
    };

    const isFormValid = subject && text && to && sender && password && selectedDossier && !disableButton;

    return (
        <div className="email-form">
            <div className="email-form__container">
                <div className="email-form__row">
                    <div className="email-form__field">
                        <label className="email-form__label">Expéditeur *</label>
                        <input
                            type="email"
                            name="sender"
                            value={sender}
                            onChange={handleChange}
                            className="email-form__input"
                            placeholder="votre.email@example.com"
                            required
                            disabled={disableButton}
                        />
                    </div>

                    <div className="email-form__field">
                        <label className="email-form__label">Mot de passe *</label>
                        <div className="email-form__password-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={password}
                                onChange={handleChange}
                                className="email-form__input"
                                placeholder="Votre mot de passe"
                                required
                                disabled={disableButton}
                            />
                            <button
                                type="button"
                                className="email-form__password-toggle"
                                onClick={togglePasswordVisibility}
                                aria-label="Afficher/masquer le mot de passe"
                                disabled={disableButton}
                            >
                                {showPassword ? (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sélection du dossier */}
                <div className="email-form__field">
                    <label className="email-form__label">Dossier *</label>
                    <select
                        name="selectedDossier"
                        value={selectedDossier || ""}
                        onChange={handleChange}
                        className="email-form__select"
                        style={{
                            width: '100%',
                            padding: '0.7rem 2.5rem 0.7rem 1rem',
                            border: '2px solid #e1e5e9',
                            borderRadius: '8px',
                            backgroundColor: '#ffffff',
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.5rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1.5rem 1.5rem',
                            fontSize: '1rem',
                            color: '#1f2937',
                            cursor: 'pointer',
                            appearance: 'none',
                            outline: 'none'
                        }}
                        required
                        disabled={disableButton}
                    >
                        <option value="">Sélectionner un dossier</option>
                        {dossier && dossier.length > 0 ? (
                            dossier.map((item) => (
                                <option key={item.ndossier} value={item.ndossier}>
                                    {item.ndossier || 'Nom défini'}
                                </option>
                            ))
                        ) : (
                            <option value="" disabled>Aucun dossier disponible</option>
                        )}
                    </select>
                </div>

                <EmailTagInput
                    name="to"
                    value={to}
                    onChange={handleChange}
                    placeholder="destinataire@example.com"
                    label="Destinataire"
                    required={true}
                    disabled={disableButton}
                />

                <EmailTagInput
                    name="cc"
                    value={cc}
                    onChange={handleChange}
                    placeholder="copie@example.com"
                    label="Cc"
                    disabled={disableButton}
                />

                <EmailTagInput
                    name="cci"
                    value={cci}
                    onChange={handleChange}
                    placeholder="copie.cachee@example.com"
                    label="Cci"
                    disabled={disableButton}
                />

                <div className="email-form__field">
                    <label className="email-form__label">Objet</label>
                    <input
                        type="text"
                        name="subject"
                        value={subject}
                        onChange={handleChange}
                        className="email-form__input"
                        placeholder="Objet du message"
                        disabled={disableButton}
                    />
                </div>

                <div className="email-form__field">
                    <label className="email-form__label">Message</label>
                    <textarea
                        name="text"
                        value={text}
                        onChange={handleChange}
                        className="email-form__textarea"
                        placeholder="Votre message..."
                        rows={8}
                        disabled={disableButton}
                    />
                </div>

                <div className="email-form__bottom">
                    <div className="email-form__controls-row">
                        <div className="email-form__field email-form__field--file">
                            <label className="email-form__label">Fichiers à joindre</label>
                            <input
                                type="file"
                                name="files"
                                onChange={handleFileChange}
                                className="email-form__file-input"
                                multiple
                                disabled={disableButton}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                            />
                        </div>

                        <div className="email-form__buttons">
                            {disableButton && <LoadingOverlay />}

                            <button
                                type="button"
                                className={`email-form__btn email-form__btn--primary ${!isFormValid ? 'email-form__btn--disabled' : ''}`}
                                onClick={handleOpenDialog}
                                disabled={!isFormValid}
                            >
                                Envoyer
                            </button>

                            <button
                                type="button"
                                className="email-form__btn email-form__btn--secondary"
                                onClick={handleReset}
                                disabled={disableButton}
                            >
                                Annuler
                            </button>
                        </div>
                    </div>

                    {/* Boutons de sélection automatique avec couleurs */}
                    <div className="email-form__auto-select">
                        <span className="email-form__auto-select-label">Sélection rapide :</span>
                        <div className="email-form__auto-select-buttons">
                            {Object.entries(documentColors).filter(([key]) => key !== 'manual').map(([type, color]) => {
                                const state = buttonStates[type];
                                const displayNames = {
                                    devis: 'Devis',
                                    contrat: 'Contrat',
                                    pgc: 'PGC',
                                    prediuo: 'Pré DIUO',
                                    diuo: 'DIUO',
                                    vic: 'VIC',
                                    rj: 'RJ'
                                };

                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        className="email-form__auto-btn"
                                        onClick={() => handleAutoSelectWithState(type)}
                                        disabled={disableButton || selectedDossier === ''}
                                        title={disableButton || selectedDossier === '' ? `Veuillez choisir un dossier` : `Sélectionner automatiquement les fichiers ${displayNames[type]}`}
                                        style={getButtonStyle(type)}
                                        {...getButtonHoverHandlers(type)}
                                    >
                                        {displayNames[type]}
                                        {state.hasFiles && (
                                            <span style={{
                                                marginLeft: '0.5rem',
                                                fontSize: '0.75rem',
                                                opacity: 0.8
                                            }}>✓</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {files && files.length > 0 && (
                        <div className="email-form__files-display">
                            <span className="email-form__files-count">
                                {files.length} fichier{files.length > 1 ? 's' : ''} sélectionné{files.length > 1 ? 's' : ''}
                            </span>
                            <div className="email-form__files-grid">
                                {files.map((file, index) => {
                                    const isPdf = file.type === 'application/pdf';
                                    const isImage = file.type.startsWith('image/');
                                    const isClickable = isPdf || isImage;
                                    const fileType = getFileType ? getFileType(file.name) : getFileTypeLocal(file.name);
                                    const fileColor = documentColors[fileType];

                                    const handleFileClick = () => {
                                        if (isPdf && showPDF) {
                                            showPDF(file);
                                        } else if (isImage && showImage) {
                                            showImage(file);
                                        }
                                    };

                                    const getTooltip = () => {
                                        if (isPdf) return `Cliquer pour afficher le PDF ${file.name}`;
                                        if (isImage) return `Cliquer pour afficher l'image ${file.name}`;
                                        return file.name;
                                    };

                                    return (
                                        <div
                                            key={index}
                                            className="email-form__file-card"
                                            style={{
                                                borderLeft: `4px solid ${fileColor}`,
                                                backgroundColor: `${fileColor}08`
                                            }}
                                        >
                                            <span
                                                className={`email-form__file-name ${isPdf ? 'email-form__file-name--pdf' : ''} ${isImage ? 'email-form__file-name--image' : ''}`}
                                                onClick={isClickable ? handleFileClick : undefined}
                                                title={getTooltip()}
                                                style={{ color: fileColor }}
                                            >
                                                {file.name}
                                            </span>
                                            <button
                                                type="button"
                                                className="email-form__file-delete"
                                                onClick={() => handleRemoveFile(index)}
                                                disabled={disableButton}
                                                aria-label={`Supprimer ${file.name}`}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MailParams;